import { useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useArticles } from "@/hooks/useArticles";
import SectionHeader from "@/components/base/SectionHeader";

export default function StartupsSection() {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const { articles: startups, loading } = useArticles({ category: "Startup", limit: 8 });

  const scroll = (dir: "left" | "right") => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir === "right" ? 420 : -420, behavior: "smooth" });
    }
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <SectionHeader
          title={t("section.startups.title")}
          subtitle={t("section.startups.subtitle")}
          viewAllLink="/startups"
          accent="green"
        />

        <div className="relative">
          <button
            onClick={() => scroll("left")}
            className="hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center rounded-full bg-white border border-gray-200 text-gray-600 hover:bg-orange-50 hover:text-orange-500 hover:border-orange-200 transition-colors cursor-pointer shadow-sm"
          >
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>
          <button
            onClick={() => scroll("right")}
            className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center rounded-full bg-white border border-gray-200 text-gray-600 hover:bg-orange-50 hover:text-orange-500 hover:border-orange-200 transition-colors cursor-pointer shadow-sm"
          >
            <i className="ri-arrow-right-s-line text-xl"></i>
          </button>

          <div
            ref={scrollRef}
            className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {loading
              ? Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="flex-shrink-0 w-80 bg-gray-100 rounded-2xl h-72 animate-pulse"></div>
                ))
              : startups.map((startup) => (
                  <Link
                    key={startup.id}
                    to={`/article/${startup.id}`}
                    className="group flex-shrink-0 w-80 bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-green-200 hover:-translate-y-1 transition-all duration-300 cursor-pointer block"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={startup.image}
                        alt={startup.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                      <div className="absolute top-3 left-3">
                        <span className="bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                          Startup
                        </span>
                      </div>
                      <div className="absolute bottom-3 right-3 bg-white/20 backdrop-blur-sm rounded-full px-3 py-1 text-white text-xs font-medium">
                        {startup.countryFlag} {startup.country}
                      </div>
                    </div>
                    <div className="p-5">
                      <h3 className="font-bold text-gray-900 text-base leading-snug line-clamp-2 group-hover:text-green-600 transition-colors mb-2">
                        {startup.title}
                      </h3>
                      <p className="text-sm text-gray-500 line-clamp-2 mb-4">{startup.summary}</p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        <span className="text-xs bg-green-50 text-green-700 px-3 py-1 rounded-full font-medium">
                          {startup.sector}
                        </span>
                        <span className="text-xs bg-gray-100 text-gray-600 px-3 py-1 rounded-full">
                          <i className="ri-user-line mr-1"></i>{startup.entrepreneur}
                        </span>
                      </div>
                      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                        <span className="text-xs text-gray-400">{t("startup.source")} {startup.source}</span>
                        <span className="text-green-600 text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                          {t("startup.discover")} <i className="ri-arrow-right-line"></i>
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
          </div>
        </div>
      </div>
    </section>
  );
}
